import {useEffect, useState} from "react";

import {clearCache, withCache} from "../cache";
import {useApi} from "../PlayMakersProvider";
import {ExtendedUserReward, UserReward, UserRewardState} from "../types";

type fetchUserRewardMethod = (quiet?: boolean, refresh?: boolean) => Promise<UserReward>;
type updateUserRewardStateMethod = (state: string) => Promise<UserReward>;
interface UseUserRewardReturn {
  refresh: fetchUserRewardMethod;
  updateUserRewardState: updateUserRewardStateMethod;
  userReward: UserReward | null;
  fetched: boolean;
}

export const useUserReward = (id?: string): UseUserRewardReturn => {
  const {
    getUserReward,
    updateUserRewardState: updateUserRewardStateApi
  } = useApi();

  const [userReward, setUserReward] = useState<UserReward | null>(null);
  const [fetched, setFetched] = useState<boolean>(false);

  const fetchUserReward: fetchUserRewardMethod = async(quiet = false, refresh) => {
    if(!quiet) setFetched(false);
    if(refresh) clearCache("getUserReward", [id]);

    try {
      if(!id) throw new Error("No user reward to fetch");

      const fetchedUserReward = await withCache(getUserReward, [id], 120_000);
      setUserReward(fetchedUserReward);

      return fetchedUserReward;
    } catch (err) {
      console.error("Error fetching user reward", err);
      throw new Error("Error fetching user reward", {cause: err});
    } finally {
      setFetched(true);
    }
  };

  const updateUserRewardState: updateUserRewardStateMethod = async state => {
    setFetched(false);

    try {
      if(!userReward?.id) throw new Error("No user reward to update");

      const updatedUserReward = await updateUserRewardStateApi(userReward.id, state);
      setUserReward(updatedUserReward);

      clearCache("getUserReward", [id]);
      clearCache("getUserRewards");
      clearCache("getUserRewardsByProject");
      clearCache("getUserRewardsByReward");

      return updatedUserReward;
    } catch (err) {
      console.error("Error updating rewards state", err);
      throw new Error("Error updating rewards state", {cause: err});
    } finally {
      setFetched(true);
    }
  };

  useEffect(() => {
    if(id) fetchUserReward();
  }, [id]);

  return {
    fetched,
    refresh: quiet => fetchUserReward(quiet, true),
    updateUserRewardState,
    userReward
  };
};


type FetchUserRewardsMethod = (quiet?: boolean, refresh?: boolean) => Promise<UserReward[]>;
type PushUserRewardsStateMethod = (id: string, state: UserRewardState) => Promise<UserReward>;
interface UseUserRewardsReturn {
  refresh: FetchUserRewardsMethod;
  userRewards: ExtendedUserReward[];
  fetched: boolean;

  // @deprecated use userRewards instead
  userRewardList: ExtendedUserReward[];
}

export const useUserRewards = (
  params: {
    projectId?: string;
    rewardId?: string;
    [key: string]: any
  } = {}
): UseUserRewardsReturn => {
  const {
    getUserRewards,
    getUserRewardsByProject,
    getUserRewardsByReward,
    updateUserRewardState: updateUserRewardStateApi
  } = useApi();

  const [userRewards, setUserRewards] = useState<ExtendedUserReward[]>([]);
  const [fetched, setFetched] = useState<boolean>(false);

  const extendUserReward = (userRewards: UserReward[]): ExtendedUserReward[] => userRewards.map(userReward => ({
    ...userReward,
    pushState: state => pushState(userReward.id, state)
  }));

  const fetchUserRewards: FetchUserRewardsMethod = async(quiet = false, refresh) => {
    if(!quiet) setFetched(false);
    if(refresh) {
      clearCache("getUserRewards");
      clearCache("getUserRewardsByProject");
      clearCache("getUserRewardsByReward");
    }

    try {
      let userRewards;
      const paramsCopy = {...params};

      if(paramsCopy.projectId) {
        const projectId = paramsCopy.projectId;
        delete paramsCopy.projectId;
        userRewards = await withCache(getUserRewardsByProject, [paramsCopy, projectId], 120_000);
      } else if(paramsCopy.rewardId) {
        const rewardId = paramsCopy.rewardId;
        delete paramsCopy.rewardId;
        userRewards = await withCache(getUserRewardsByReward, [rewardId, paramsCopy], 120_000);
      } else
        userRewards = await withCache(getUserRewards, [paramsCopy], 120_000);

      setUserRewards(extendUserReward(userRewards));
      return userRewards;
    } catch (err) {
      console.error("Error fetching user rewards", err);
      throw new Error("Error fetching user rewards", {cause: err});
    } finally {
      setFetched(true);
    }
  };


  const pushState: PushUserRewardsStateMethod = async(id, state) => {
    try {
      const userReward = await updateUserRewardStateApi(id, state);
      if(!userReward) throw new Error("User reward state not updated");

      updateUserRewardState(id, state);

      clearCache("getUserRewards");
      clearCache("getUserRewardsByProject");
      clearCache("getUserRewardsByReward");

      return userReward;
    } catch (error) {
      console.error(`Error updating state for user reward '${id}'`, error);
      throw new Error(`Error updating state for user reward '${id}'`, {cause: error});
    }
  };


  const updateUserRewardState = (
    userRewardId: string,
    state: UserRewardState
  ) => {
    setUserRewards(prevRewards =>
      prevRewards.map(reward =>
        reward.id === userRewardId ? {...reward, state} : reward
      )
    );
  };

  useEffect(() => {
    fetchUserRewards();
  }, [JSON.stringify(params)]);

  return {
    fetched,
    refresh: quiet => fetchUserRewards(quiet, true),
    userRewardList: userRewards,
    userRewards
  };
};

export const useUserRewardsByProject =
  (projectId: string, params?: {[key: string]: any}) => useUserRewards({...params, projectId});
export const useUserRewardsByReward =
  (rewardId: string, params?: {[key: string]: any}) => useUserRewards({...params, rewardId});

// Aliases to deprecate
export const useUserRewardsByProjectId =
  (projectId: string, params?: {[key: string]: any}) => useUserRewards({...params, projectId});
export const fetchUserRewardsByRewardId =
  (rewardId: string, params?: {[key: string]: any}) => useUserRewards({...params, rewardId});
